/**
 * Effect catalog: what the timeline can animate on each element type.
 *
 * Every EffectDef knows how to read and write its target, which interpolation
 * suits it, an optional identity ("no effect") value, and optional config
 * knobs that are tuned once rather than keyframed. The track manager turns a
 * def into a PropertyAnimTrack on first use (createTrackForEffect).
 *
 * Splat shader effects write to the per-splat parameter store
 * (splat-effects.ts); the projector reads that store each frame.
 */

import { BLEND_NONE, BLEND_NORMAL, Color, Quat, Vec3 } from 'playcanvas';

import { Element, ElementType } from '../element';
import type { Events } from '../events';
import type { Scene } from '../scene';
import type { Splat } from '../splat';
import type { Model } from './model';
import { PropertyAnimTrack, type EffectAdapter, type Interpolation, type ValueKind } from './property-anim-track';
import { getSplatEffect, setSplatEffect } from './splat-effects';

export interface ConfigParam {
    label: string;
    kind: ValueKind;
    range?: [number, number];
    /** Plain-English explanation, shown as the row's help tooltip. */
    description?: string;
    get(element: Element, scene: Scene): number[];
    set(element: Element, scene: Scene, value: number[]): void;
}

export interface EffectDef {
    id: string;
    name: string;
    kind: ValueKind;
    interpolation: Interpolation;
    /** Config knobs shown for the active track — tuned once, not keyframed. */
    config?: ConfigParam[];
    /** "No effect" value restored when the last key is removed. */
    identity?: number[];
    makeAdapter(element: Element, scene: Scene): EffectAdapter;
    /** Fired when the first key lands (turn a compound effect on). */
    onActivate?(element: Element, scene: Scene): void;
    /** Fired when the last key is removed (turn it back off). */
    onDeactivate?(element: Element, scene: Scene): void;
    /** Slider range for number effects. */
    range?: [number, number];
}

type Placed = Splat | Model;

// ── adapters ────────────────────────────────────────────────────────────

/** Splats keep GPU-side bounds in step through move(); models mark theirs dirty the same way. */
const commitTransform = (el: Element) => {
    (el as Placed).move?.();
};

const numAdapter = (target: any, prop: string): EffectAdapter => ({
    dim: 1,
    get: () => [target[prop] as number],
    set: (v) => { target[prop] = v[0]; }
});

const matNumAdapter = (mat: any, prop: string, def: number): EffectAdapter => ({
    dim: 1,
    get: () => [typeof mat?.[prop] === 'number' ? mat[prop] : def],
    set: (v) => {
        if (!mat) return;
        mat[prop] = v[0];
        mat.update();
    }
});

const matOpacityAdapter = (mat: any): EffectAdapter => ({
    dim: 1,
    get: () => [typeof mat?.opacity === 'number' ? mat.opacity : 1],
    set: (v) => {
        if (!mat) return;
        const a = v[0];
        mat.opacity = a;
        mat.blendType = a < 0.999 ? BLEND_NORMAL : BLEND_NONE;
        mat.depthWrite = a >= 0.999;
        mat.update();
    }
});

const vec3Adapter = (getVec: () => Vec3, setVec: (x: number, y: number, z: number) => void): EffectAdapter => ({
    dim: 3,
    get: () => {
        const v = getVec();
        return [v.x, v.y, v.z];
    },
    set: v => setVec(v[0], v[1], v[2])
});

const colorAdapter = (getColor: () => Color, setColor: (c: Color) => void): EffectAdapter => {
    const tmp = new Color();
    return {
        dim: 3,
        get: () => {
            const c = getColor();
            return [c.r, c.g, c.b];
        },
        set: (v) => {
            tmp.set(v[0], v[1], v[2], 1);
            setColor(tmp);
        }
    };
};

// spin: rest orientation * axis-angle, so a turntable never accumulates drift
const spinRest = new WeakMap<Element, Quat>();
const spinAxis = new WeakMap<Element, Vec3>();
const spinAngle = new WeakMap<Element, number>();
const spinDelta = new Quat();
const spinOut = new Quat();
const spinAxisTmp = new Vec3();

const applySpin = (el: Element) => {
    const entity = (el as Placed).entity;
    const rest = spinRest.get(el) ?? entity.getLocalRotation();
    const axis = spinAxis.get(el) ?? new Vec3(0, 1, 0);
    const angle = spinAngle.get(el) ?? 0;
    spinAxisTmp.copy(axis);
    if (spinAxisTmp.length() < 1e-6) spinAxisTmp.set(0, 1, 0);
    spinAxisTmp.normalize();
    spinDelta.setFromAxisAngle(spinAxisTmp, angle);
    spinOut.mul2(rest, spinDelta);
    entity.setLocalRotation(spinOut);
    commitTransform(el);
};

const spinAdapter = (el: Element): EffectAdapter => ({
    dim: 1,
    get: () => [spinAngle.get(el) ?? 0],
    set: (v) => {
        spinAngle.set(el, v[0]);
        applySpin(el);
    }
});

// ── shared: entity transform effects (splats and models) ────────────────

const entityTransformEffects = (): EffectDef[] => [
    {
        id: 'position',
        name: 'Position',
        kind: 'vec3',
        interpolation: 'cubic',
        makeAdapter: (el) => {
            const entity = (el as Placed).entity;
            return vec3Adapter(
                () => entity.getLocalPosition(),
                (x, y, z) => {
                    entity.setLocalPosition(x, y, z);
                    commitTransform(el);
                }
            );
        }
    },
    {
        id: 'rotation',
        name: 'Rotation (Euler °)',
        kind: 'vec3',
        interpolation: 'cubic',
        makeAdapter: (el) => {
            const entity = (el as Placed).entity;
            return vec3Adapter(
                () => entity.getLocalEulerAngles(),
                (x, y, z) => {
                    entity.setLocalEulerAngles(x, y, z);
                    commitTransform(el);
                }
            );
        }
    },
    {
        id: 'spin-local',
        name: 'Spin (local axis)',
        kind: 'number',
        interpolation: 'cubic',
        range: [-1080, 1080],
        identity: [0],
        onActivate: (el) => {
            const entity = (el as Placed).entity;
            if (!spinRest.has(el)) spinRest.set(el, entity.getLocalRotation().clone());
            if (!spinAxis.has(el)) spinAxis.set(el, new Vec3(0, 1, 0));
        },
        onDeactivate: (el) => {
            const rest = spinRest.get(el);
            if (rest) {
                (el as Placed).entity.setLocalRotation(rest);
                commitTransform(el);
            }
            spinAngle.delete(el);
        },
        makeAdapter: el => spinAdapter(el),
        config: [
            {
                label: 'Spin axis (local)',
                kind: 'vec3',
                description: 'Axis to spin around, in the element\'s own frame. 0,1,0 is its up axis (turntable); 1,0,0 pitches, 0,0,1 rolls.',
                get: (el) => {
                    const a = spinAxis.get(el) ?? new Vec3(0, 1, 0);
                    return [a.x, a.y, a.z];
                },
                set: (el, scene, v) => {
                    spinAxis.set(el, new Vec3(v[0], v[1], v[2]));
                    applySpin(el);
                    scene.forceRender = true;
                }
            },
            {
                label: 'Rest orientation °',
                kind: 'vec3',
                description: 'The base orientation the spin is applied on top of. Defaults to the orientation when the effect was added; angle 0 is exactly this pose.',
                get: (el) => {
                    const q = spinRest.get(el) ?? (el as Placed).entity.getLocalRotation();
                    const e = new Vec3();
                    q.getEulerAngles(e);
                    return [e.x, e.y, e.z];
                },
                set: (el, scene, v) => {
                    const q = new Quat();
                    q.setFromEulerAngles(v[0], v[1], v[2]);
                    spinRest.set(el, q);
                    applySpin(el);
                    scene.forceRender = true;
                }
            }
        ]
    },
    {
        id: 'scale',
        name: 'Scale',
        kind: 'vec3',
        interpolation: 'cubic',
        range: [0.01, 10],
        makeAdapter: (el) => {
            const entity = (el as Placed).entity;
            return vec3Adapter(
                () => entity.getLocalScale(),
                (x, y, z) => {
                    entity.setLocalScale(x, y, z);
                    commitTransform(el);
                }
            );
        }
    },
    {
        id: 'visible',
        name: 'Visible',
        kind: 'bool',
        interpolation: 'step',
        makeAdapter: el => ({
            dim: 1,
            get: () => [(el as Placed).visible ? 1 : 0],
            set: (v) => {
                (el as Placed).visible = v[0] >= 0.5;
            }
        })
    }
];

// ── splat shader effects (per-splat parameter store) ─────────────────────

const storeAdapter = (el: Element, name: string, dim = 1): EffectAdapter => ({
    dim,
    get: () => getSplatEffect(el, name).slice(0, dim),
    set: v => setSplatEffect(el, name, v.slice(0, dim))
});

const storeConfig = (label: string, name: string, kind: ValueKind, range?: [number, number], description?: string): ConfigParam => {
    const dim = kind === 'number' ? 1 : 3;
    return {
        label,
        kind,
        range,
        description,
        get: el => getSplatEffect(el, name).slice(0, dim),
        set: (el, scene, v) => {
            setSplatEffect(el, name, v.slice(0, dim));
            scene.forceRender = true;
        }
    };
};

const setFlags = (el: Element, flags: Record<string, number>) => {
    for (const [name, value] of Object.entries(flags)) setSplatEffect(el, name, [value]);
};

const REVEALS: Array<{ id: string; name: string; time: string; enabled: string; range: [number, number]; config: ConfigParam[] }> = [
    {
        id: 'splat-radial-reveal',
        name: 'Radial wave reveal',
        time: 'uRadialTime',
        enabled: 'uRadialEnabled',
        range: [0, 20],
        config: [
            storeConfig('Center', 'uRadialCenter', 'vec3', undefined, 'World-space origin both waves expand from.'),
            storeConfig('Speed', 'uRadialSpeed', 'number', [0.1, 50], 'Wave speed in world units per second.'),
            storeConfig('Acceleration', 'uRadialAcceleration', 'number', [0, 20], 'Speed-up over time, so the wave finishes in finite time on large scenes.'),
            storeConfig('Lift delay', 'uRadialDelay', 'number', [0, 10], 'Seconds between the dot wave and the lift wave that turns dots into full splats.'),
            storeConfig('End radius', 'uRadialEndRadius', 'number', [1, 500], 'Beyond this distance from the center splats stay hidden. Roughly the scene radius.'),
            storeConfig('Band width', 'uRadialBandWidth', 'number', [0.1, 10], 'Thickness of each wave\'s colour band.'),
            storeConfig('Oscillation', 'uRadialOscillation', 'number', [0, 2], 'Vertical bob inside the band.'),
            storeConfig('Dot tint', 'uRadialDotTint', 'color', undefined, 'Colour of the leading dot wave.'),
            storeConfig('Dot tint mix', 'uRadialDotTintMix', 'number', [0, 1], '0 keeps the splat colour, 1 is the full tint.'),
            storeConfig('Dot size', 'uRadialDotSize', 'number', [0.001, 1], 'World size of the dots before the lift wave.'),
            storeConfig('Lift tint', 'uRadialLiftTint', 'color', undefined, 'Glow colour as the lift wave passes.'),
            storeConfig('Lift tint mix', 'uRadialLiftTintMix', 'number', [0, 1], '0 reveals at the original colour, 1 is the full glow.'),
            storeConfig('Engine mode', 'uRadialEngineMode', 'number', [0, 1], '1 hides splats ahead of the dot wave (the engine demo look); 0 shows them as vibrating dots.')
        ]
    },
    {
        id: 'splat-rain-reveal',
        name: 'Rain reveal',
        time: 'uRainTime',
        enabled: 'uRainEnabled',
        range: [0, 20],
        config: [
            storeConfig('Center', 'uRainCenter', 'vec3', undefined, 'Origin of the 2D wave that triggers each splat\'s fall.'),
            storeConfig('Height', 'uRainDistance', 'number', [1, 200], 'How far above its resting place a splat starts.'),
            storeConfig('Speed', 'uRainSpeed', 'number', [0.1, 50], 'Wave speed in world units per second.'),
            storeConfig('Acceleration', 'uRainAcceleration', 'number', [0, 20], 'Wave speed-up over time.'),
            storeConfig('Flight time', 'uRainFlightTime', 'number', [0.1, 10], 'Seconds a splat takes to fall.'),
            storeConfig('Drop size', 'uRainSize', 'number', [0.001, 1], 'World size of a splat while falling.'),
            storeConfig('Rotation', 'uRainRotation', 'number', [0, 2], 'Turns around the wave center during the fall.'),
            storeConfig('Fall tint', 'uRainFallTint', 'color', undefined, 'Tint while falling.'),
            storeConfig('Fall tint mix', 'uRainFallTintIntensity', 'number', [0, 1], 'Strength of the fall tint.'),
            storeConfig('Landing flash', 'uRainHitTint', 'color', undefined, 'Additive flash on landing (values above 1 bloom).'),
            storeConfig('Flash duration', 'uRainHitDuration', 'number', [0.05, 3], 'Seconds the landing flash lasts.'),
            storeConfig('End radius', 'uRainEndRadius', 'number', [1, 500], 'Beyond this distance from the center nothing animates.')
        ]
    },
    {
        id: 'splat-grid-reveal',
        name: 'Grid eruption reveal',
        time: 'uGridTime',
        enabled: 'uGridEnabled',
        range: [0, 20],
        config: [
            storeConfig('Center', 'uGridCenter', 'vec3', undefined, 'Point the blocks erupt from.'),
            storeConfig('Blocks per axis', 'uGridBlockCount', 'number', [1, 64], 'Grid divisions per axis.'),
            storeConfig('Block size', 'uGridBlockSize', 'number', [0.1, 50], 'Edge length of one block.'),
            storeConfig('Block delay', 'uGridDelay', 'number', [0, 2], 'Seconds between successive blocks.'),
            storeConfig('Travel time', 'uGridDuration', 'number', [0.1, 10], 'Seconds a splat travels from the center to its place.'),
            storeConfig('Dot size', 'uGridDotSize', 'number', [0.001, 1], 'World size while travelling.'),
            storeConfig('Move tint', 'uGridMoveTint', 'color', undefined, 'Tint while travelling.'),
            storeConfig('Move tint mix', 'uGridMoveTintIntensity', 'number', [0, 1], 'Strength of the travel tint.'),
            storeConfig('Landing flash', 'uGridLandTint', 'color', undefined, 'Additive flash on landing.'),
            storeConfig('Flash duration', 'uGridLandDuration', 'number', [0.05, 3], 'Seconds the landing flash lasts.'),
            storeConfig('End radius', 'uGridEndRadius', 'number', [1, 500], 'Beyond this distance from the center nothing animates.')
        ]
    }
];

export const getSplatEffects = (scene: Scene): EffectDef[] => {
    const fr = () => { scene.forceRender = true; };
    const splatExtras: EffectDef[] = [
        {
            id: 'splat-fade',
            name: 'Fade alpha',
            kind: 'number',
            interpolation: 'linear',
            range: [0, 1],
            identity: [1],
            makeAdapter: el => storeAdapter(el, 'uFadeAlpha')
        },
        {
            id: 'splat-dissolve',
            name: 'Dissolve',
            kind: 'number',
            interpolation: 'linear',
            range: [0, 1],
            identity: [0],
            makeAdapter: el => storeAdapter(el, 'uDissolveAmount')
        },
        {
            id: 'splat-grow',
            name: 'Grow scale',
            kind: 'number',
            interpolation: 'linear',
            range: [0, 5],
            identity: [1],
            makeAdapter: el => storeAdapter(el, 'uGrowScale')
        },
        {
            id: 'splat-reveal-radius',
            name: 'Reveal radius',
            kind: 'number',
            interpolation: 'linear',
            range: [0, 50],
            identity: [1e9],
            makeAdapter: el => storeAdapter(el, 'uRevealRadius'),
            config: [
                storeConfig('Center', 'uRevealCenter', 'vec3', undefined, 'Splats farther than the radius from this point are hidden.'),
                storeConfig('Edge softness', 'uRevealFade', 'number', [0, 10], 'Width of the fade at the radius.')
            ]
        },
        {
            id: 'splat-particle-reveal',
            name: 'Particle reveal',
            kind: 'number',
            interpolation: 'linear',
            range: [0, 1],
            identity: [1],
            makeAdapter: el => storeAdapter(el, 'uParticleProgress'),
            config: [
                storeConfig('Spread', 'uParticleSpread', 'number', [0, 50], 'How far each splat flies in from.'),
                storeConfig('Direction', 'uParticleDirection', 'vec3', undefined, 'Bias for the fly-in direction; 0,0,0 is purely radial.'),
                storeConfig('Duration', 'uParticleDuration', 'number', [0.05, 1], 'Fraction of the progress each splat spends assembling.'),
                storeConfig('Flash', 'uParticleFlash', 'number', [0, 3], 'Brightness flash as a splat lands.')
            ]
        },
        ...REVEALS.map((r): EffectDef => ({
            id: r.id,
            name: r.name,
            kind: 'number',
            interpolation: 'linear',
            range: r.range,
            identity: [0],
            onActivate: (el) => {
                setFlags(el, { uRadialEnabled: 0, uRainEnabled: 0, uGridEnabled: 0, [r.enabled]: 1 });
                fr();
            },
            onDeactivate: (el) => {
                setFlags(el, { [r.enabled]: 0 });
                fr();
            },
            makeAdapter: el => storeAdapter(el, r.time),
            config: r.config
        }))
    ];
    return [...entityTransformEffects(), ...splatExtras].map(def => ({
        ...def,
        makeAdapter: wrapWithForceRender(def.makeAdapter, fr)
    }));
};

// ── models ──────────────────────────────────────────────────────────────

const firstMeshInstance = (el: Element): any => {
    const entity = (el as Model).entity;
    let found: any = null;
    const walk = (e: any) => {
        if (found) return;
        const mis = e.render?.meshInstances;
        if (mis && mis.length) {
            found = mis[0];
            return;
        }
        for (const child of e.children ?? []) walk(child);
    };
    walk(entity);
    return found;
};

export const getModelEffects = (scene: Scene): EffectDef[] => {
    const fr = () => { scene.forceRender = true; };
    const withMat = (def: number[], make: (mat: any) => EffectAdapter) => (el: Element): EffectAdapter => {
        const mi = firstMeshInstance(el);
        if (!mi?.material) return { dim: def.length, get: () => def.slice(), set: () => {} };
        return make(mi.material);
    };
    const modelExtras: EffectDef[] = [
        { id: 'opacity', name: 'Opacity', kind: 'number', interpolation: 'linear', range: [0, 1], makeAdapter: withMat([1], m => matOpacityAdapter(m)) },
        { id: 'emissive-intensity', name: 'Emissive intensity', kind: 'number', interpolation: 'linear', range: [0, 10], makeAdapter: withMat([1], m => matNumAdapter(m, 'emissiveIntensity', 1)) },
        { id: 'emissive-color', name: 'Emissive colour', kind: 'color', interpolation: 'linear', makeAdapter: withMat([0, 0, 0], m => colorAdapter(() => m.emissive, (c) => { m.emissive.copy(c); m.update(); })) },
        { id: 'diffuse-color', name: 'Diffuse colour', kind: 'color', interpolation: 'linear', makeAdapter: withMat([1, 1, 1], m => colorAdapter(() => m.diffuse, (c) => { m.diffuse.copy(c); m.update(); })) },
        { id: 'metalness', name: 'Metalness', kind: 'number', interpolation: 'linear', range: [0, 1], makeAdapter: withMat([1], m => matNumAdapter(m, 'metalness', 1)) },
        { id: 'gloss', name: 'Gloss', kind: 'number', interpolation: 'linear', range: [0, 1], makeAdapter: withMat([0.25], m => matNumAdapter(m, 'gloss', 0.25)) },
        { id: 'bumpiness', name: 'Normal intensity', kind: 'number', interpolation: 'linear', range: [0, 2], makeAdapter: withMat([1], m => matNumAdapter(m, 'bumpiness', 1)) },
        { id: 'specular-intensity', name: 'Specularity', kind: 'number', interpolation: 'linear', range: [0, 2], makeAdapter: withMat([1], m => matNumAdapter(m, 'specularityFactor', 1)) }
    ];
    return [...entityTransformEffects(), ...modelExtras].map(def => ({
        ...def,
        makeAdapter: wrapWithForceRender(def.makeAdapter, fr)
    }));
};

/** Scene-wide effects (exposure, contrast, bloom) return with the lighting port. */
export const getSceneEffects = (_scene: Scene): EffectDef[] => [];

const wrapWithForceRender = (inner: (el: Element, scene: Scene) => EffectAdapter, fr: () => void) => {
    return (el: Element, scene: Scene): EffectAdapter => {
        const a = inner(el, scene);
        return {
            dim: a.dim,
            get: a.get,
            set: (v) => {
                a.set(v);
                fr();
            }
        };
    };
};

export const effectsForElement = (element: Element, scene: Scene): EffectDef[] => {
    if (element.type === ElementType.splat) return getSplatEffects(scene);
    if (element.type === ElementType.model) return getModelEffects(scene);
    return [];
};

export const createTrackForEffect = (events: Events, scene: Scene, element: Element, effect: EffectDef): PropertyAnimTrack => {
    return new PropertyAnimTrack({
        events,
        id: `${element.type}:${element.uid}:${effect.id}`,
        adapter: effect.makeAdapter(element, scene),
        interpolation: effect.interpolation,
        identity: effect.identity,
        onApply: () => { scene.forceRender = true; },
        onActivate: effect.onActivate ? () => effect.onActivate!(element, scene) : undefined,
        onDeactivate: effect.onDeactivate ? () => effect.onDeactivate!(element, scene) : undefined
    });
};

export { numAdapter };
